/**
 * PipelineBoard — the opportunity-based kanban (2026-09-06).
 *
 * REPLACES the contact-based board body. The old one drew crm_contacts
 * bucketed by the free-text crm_contacts.pipeline_stage; crm_contacts has NO
 * pipeline_id, so on any pipeline whose stage names did not match the contact
 * vocabulary it rendered nothing under a correct header count. Measured in
 * production 2026-09-06: Logistics showed "12 open opportunities" over an
 * empty board.
 *
 * Data comes from crm:pipeline:board — crm_opportunities joined to
 * crm_pipeline_stages through ghl_opportunity_map.ghl_stage_id, BY ID.
 * Measured: 7,728 of 7,730 open opportunities resolve by id, 100% per
 * pipeline, zero unresolvable.
 *
 * ⚠ Every stage is rendered. GHL truncates its own board at ~12 columns and
 * Patient Acquisition 2.0 declares 17; that truncation produced three false
 * findings in two days and is deliberately not copied.
 *
 * ── PLACE (Card PIPELINE-PLACE-AND-FOCUS, 10 Oct 2026) ──────────────────────
 *
 * CRMPage renders the pipeline as `{!selectedContactId && tab === 'pipeline'
 * && <PipelineTab/>}`, so clicking a card UNMOUNTS this board. Measured: on
 * return the board came back at the top of every column with 50 cards in each,
 * however many pages had been loaded and however far down the person had read.
 *
 * This component therefore REPORTS its place as it changes (`onPlaceChange`)
 * and RESTORES one it is handed (`place`). src/lib/pipelinePlace.js owns the
 * record and its storage; this file owns only the DOM side of it.
 *
 * ⚠⚠ A SCROLL OFFSET IS NOTHING WITHOUT THE ROWS UNDER IT. Restoring
 * scrollTop = 1200 into a column holding one page of cards is a silent no-op —
 * the browser clamps to the bottom and nobody sees an error. That is NAV-1's
 * defect exactly ("capture ran, restore ran, and nothing was ever restored").
 * So the extra pages are re-fetched FIRST — in ONE request per column, not one
 * per page — and the offsets are applied only after those cards are in the DOM.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight, ChevronDown, User } from 'lucide-react';
import OpportunityStatusBadge from '../../components/OpportunityStatusBadge';
import { mutedStyle, isActiveStatus, OPPORTUNITY_STATUSES, STATUS_META, hiddenStatusNote } from '../../lib/opportunityStatus';

const PAGE_SIZE = 50;
const neuShadowSm = '4px 4px 8px rgba(186,199,216,0.5), -4px -4px 8px rgba(255,255,255,0.85)';

const money = (n) =>
  Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

/**
 * ⚠⚠ EVERY money figure on this board carries its coverage. There is one
 * renderer and it is the only place a sum is printed, so a figure cannot ship
 * without its denominator by omission.
 *
 * Measured 2026-09-06: 279 of 7,730 open opportunities (3.6%) carry a value.
 * A column header reading "$980,254" over 229 cards is summed from whichever
 * few of them have one — a PEM reads it as the column's value, and it is not.
 * This is the same argument that keeps a zero-value CARD blank rather than
 * printing "$0": both would assert a number we do not have.
 *
 * Accuracy rule: MEASURED renders · PARTIAL renders WITH the fraction inline ·
 * UNKNOWN does not render. Coverage 0 returns null — no figure at all.
 */
// Card P117 PR-B — one status vocabulary, shared with the Opportunities list.
export function MoneyWithCoverage({ sum, withValue, count, style }) {
  if (!withValue || withValue <= 0) return null;      // UNKNOWN — render nothing
  return (
    <span data-money style={style}>
      <span data-money-figure>{money(sum)}</span>
      <span data-money-coverage style={{ opacity: 0.75 }}>
        {' · '}{withValue} of {count} carry a value
      </span>
    </span>
  );
}

const sinceLabel = (iso) => {
  if (!iso) return null;
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (!Number.isFinite(d) || d < 0) return null;
  return d === 0 ? 'today' : `${d}d`;
};

/** Why a column was collapsed — shown, never assumed. */
const basisLabel = {
  is_win: 'marked as a win on the stage',
  is_loss: 'marked as a loss on the stage',
  name_fallback: '⚠ inferred from the stage name — awaiting ruling',
};

// ⚠ NO DRAG. Cards are deliberately not grabbable and there is no drop target.
// A drag affordance that refuses is a promise the screen cannot keep. Drag
// arrives when the zgx_touched_at latch is field-scoped — it is ROW-scoped
// today (mig-149/163: BEFORE INSERT OR UPDATE, no column list, one scalar
// column), so one move would freeze value, contact name, status and last
// activity on that opportunity against GHL, permanently.
function Card({ card, onOpen, onFocusStage }) {
  const since = sinceLabel(card.lastActivityAt);
  return (
    <div
      onClick={(e) => {
        /*
         * ⚠ RECORD THE STAGE BEFORE OPENING, NOT AFTER — and this still holds
         * even though a drawer no longer unmounts the board.
         *
         * The original reason was that onOpen swapped the pipeline for the
         * contact record, so a report fired afterwards came from a dead
         * component. Card P95 makes the default a SLIDE-OVER, which leaves
         * this tree mounted — but a modifier-click still opens the full
         * record and still unmounts it, so the ordering is load-bearing for
         * exactly the gesture it was written for.
         */
        if (onFocusStage) onFocusStage();
        // ⚠ The EVENT is forwarded: the host reads the modifier from it, and
        // it is the only thing that can tell a preview from a full record.
        /*
         * ⛔⛔ NO OPPORTUNITY IS PASSED, SO THIS BOARD OFFERS NO "MOVE STAGE" —
         * Card P95 item 4, deliberately narrowed.
         *
         * crm:opportunities:move-stage writes crm_opportunities.stage, a FREE
         * TEXT column. THIS board does not read it: crm:pipeline:board groups
         * by ghl_opportunity_map.ghl_stage_id joined to crm_pipeline_stages
         * (routes/crm.js, the board handler). So a move made here would write
         * a value this board never displays and THE CARD WOULD NOT MOVE —
         * an affordance that refuses, which is the same promise the screen
         * cannot keep that PipelineLegacyBoard's header already records
         * ("the card moves; the report does not", measured 2026-09-06).
         *
         * Move stage is offered on the Opportunities tab, whose list and board
         * group by the same free-text stage the handler writes. Bringing it
         * here needs the two stage models reconciled first — its own card.
         */
        if (onOpen && card.contactId) onOpen(card.contactId, { event: e, opportunity: null });
      }}
      title={isActiveStatus(card.status)
        ? "Click to preview the contact — cmd/ctrl-click for the full record"
        : "This deal is closed — click to preview the contact"}
      style={{
        background: '#F8F9FA', borderRadius: 10, padding: '8px 10px', marginBottom: 6,
        boxShadow: neuShadowSm, cursor: 'pointer', transition: 'all .15s ease',
        /* Card P117 PR-B — closed deals are MUTED, never hidden. The eye
           skips them; the badge below lets it come back. */
        ...(mutedStyle(card.status) || {}),
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#2E8B98', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {card.contactName || '(no name)'}
        </div>
        {/* Only non-open deals are badged on the board: badging every card
            "Open" would be noise on a board that is mostly open work, and
            the whole question Maria asked is which ones are NOT. */}
        {!isActiveStatus(card.status) && (
          <OpportunityStatusBadge status={card.status} compact />
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginLeft: 14, marginTop: 3 }}>
        <span style={{ fontSize: 9, color: '#6C757D' }}>{card.source || '—'}</span>
        {/* Value is shown only when there IS one. 96.4% of open opportunities
            carry no value (measured 2026-09-06: 279 of 7,730), and rendering
            "$0" on all of them would assert a zero we do not know. */}
        {card.value > 0 && (
          // A SINGLE row's own value — coverage is 1 of 1, so no fraction. It
          // renders only when a value exists (never "$0"), which is the same
          // rule the aggregate renderer applies one level up.
          <span data-card-value style={{ fontSize: 11, fontWeight: 700, color: '#15803D' }}>{money(card.value)}</span>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginLeft: 14, marginTop: 3 }}>
        <span style={{ fontSize: 9, color: '#ADB5BD', display: 'flex', alignItems: 'center', gap: 3 }}>
          <User size={9} /> {card.ownerName || 'Unassigned'}
        </span>
        {since && <span style={{ fontSize: 9, color: '#ADB5BD' }}>{since}</span>}
      </div>
    </div>
  );
}

export default function PipelineBoard({
  pipelineId, barFilter, onContactClick,
  // Place (Card PIPELINE-PLACE-AND-FOCUS). Both optional: every existing
  // caller and every existing test keeps working with no place at all, and the
  // board then behaves exactly as it did before this card.
  place = null, onPlaceChange = null,
}) {
  const [state, setState] = useState({ loading: true, error: null, columns: [], unplacedCount: 0 });
  const [expandedTerminal, setExpandedTerminal] = useState({});
  const [extra, setExtra] = useState({});          // stageId -> extra cards loaded
  const [loadingMore, setLoadingMore] = useState({});

  const ownerFilter = (barFilter && barFilter.owner) || null;
  const searchFilter = (barFilter && barFilter.search) || '';
  /*
   * ⛔⛔ THE BOARD'S STATUS FILTER — Card P117 PR-B.
   *
   * It existed before this card; it was just invisible and unchangeable,
   * hard-coded `status = 'open'` in BOTH board handlers. The default here is
   * byte-identical to that, so an untouched board shows exactly what it
   * always did — but now the filter is on screen, adjustable, and says what
   * it is excluding.
   */
  const [statuses, setStatuses] = useState(['open']);
  const statusKey = statuses.join(',');
  const toggleStatus = useCallback((s) => {
    setStatuses((cur) => {
      const next = cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s];
      // ⚠ Never leave the board with NO status selected: an empty selection
      // reaches the server as "default", which is ['open'] — so the chips
      // would say "nothing" while the board showed open deals. Refusing the
      // last removal keeps the control and the rows agreeing.
      return next.length ? next : cur;
    });
  }, []);

  // ── Place plumbing ───────────────────────────────────────────────────────
  // The place to restore is read ONCE PER PIPELINE, from a ref. Reading it from
  // the prop on every render would re-restore the scroll underneath someone who
  // had just scrolled away — the "latched once per page load" lesson from Card
  // PARTNER-LIST-REFRESH, which broke seven suites before it was latched.
  const restoreRef = useRef(null);
  const stripRef = useRef(null);                 // the horizontal column strip
  const columnRefs = useRef({});                 // stageId -> the vertical scroller
  const reportRef = useRef(onPlaceChange);
  reportRef.current = onPlaceChange;
  const report = useCallback((fn) => {
    if (reportRef.current) reportRef.current(fn);
  }, []);
  const noteStage = useCallback((stageId) => {
    report((p) => ({ ...p, stageId: String(stageId) }));
  }, [report]);

  useEffect(() => {
    // A new pipeline means a new place. `place` is captured here and nowhere
    // else, so later re-renders (a filter change, a Load more) cannot re-arm it.
    restoreRef.current = place || null;
    /*
     * ⚠ `place` IS DELIBERATELY NOT A DEPENDENCY — that is the latch, not an
     * oversight. Re-arming on every new `place` identity would re-restore the
     * scroll underneath someone who had just scrolled away.
     *
     * ⛔ AND NO `eslint-disable` HERE. `react-hooks/exhaustive-deps` is NOT a
     * configured rule in this project — only `rules-of-hooks` is — so disabling
     * it is itself a build ERROR ("Definition for rule … was not found"). The
     * same note is at CRMPage.js:1292 and ReputationTab.js:46; I reintroduced
     * the mistake here and `npm run build` was the only gate that saw it —
     * the unit and react suites pass either way.
     */
  }, [pipelineId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setExtra({});
        const r = await window.zignagenixAPI.invoke('crm:pipeline:board', pipelineId,
          { pageSize: PAGE_SIZE, ownerId: ownerFilter, search: searchFilter, statuses });
        if (cancelled) return;
        if (!r || r.error) { setState({ loading: false, error: (r && r.error) || 'load failed', columns: [], unplacedCount: 0 }); return; }

        /*
         * ⚠⚠ THE PAGES BEFORE THE OFFSETS. A column the person had paged three
         * times holds 150 cards; restoring its scrollTop against the 50 this
         * response carries would clamp to the bottom and look like the restore
         * "worked". One request per such column, asking for the whole missing
         * run at once — never one request per page.
         */
        const want = (restoreRef.current && restoreRef.current.loadedExtra) || {};
        const refilled = {};
        for (const col of (r.columns || [])) {
          const n = Number(want[String(col.stageId)] || 0);
          if (!n) continue;
          try {
            const more = await window.zignagenixAPI.invoke('crm:pipeline:board:column',
              pipelineId, col.stageId, (col.cards || []).length, n,
              { ownerId: ownerFilter, search: searchFilter, statuses });
            if (more && Array.isArray(more.cards) && more.cards.length) refilled[col.stageId] = more.cards;
          } catch { /* a column that will not refill restores at the top — never a blank board */ }
        }
        if (cancelled) return;
        if (Object.keys(refilled).length) setExtra(refilled);
        setState({ loading: false, error: null, columns: r.columns || [], unplacedCount: r.unplacedCount || 0 });
      } catch (e) {
        if (!cancelled) setState({ loading: false, error: e.message, columns: [], unplacedCount: 0 });
      }
    })();
    return () => { cancelled = true; };
    // ⚠ statusKey, not the array: a new [] identity every render would
    // refetch the board on every commit.
    /* ⚠ NO eslint-disable DIRECTIVE HERE. `react-hooks/exhaustive-deps` is not
       loaded by the production build's ESLint, so a dangling disable-line is a
       HARD BUILD FAILURE ("Definition for rule ... was not found") — the exact
       break the production-build CI job was added for in #121. Measured here:
       adding one failed `CI=true react-scripts build` while jest stayed green. */
  }, [pipelineId, ownerFilter, searchFilter, statusKey]);

  /*
   * Apply the offsets, once, after the cards above are on the page.
   *
   * ⛔ CONSUMED, NOT JUST READ. restoreRef is cleared as the offsets go on, so
   * a later re-render (a Load more, a filter change, a parent repaint) cannot
   * yank the person back to where they were ten seconds ago.
   */
  useEffect(() => {
    const want = restoreRef.current;
    if (!want || state.loading) return;
    restoreRef.current = null;
    const apply = () => {
      try {
        if (stripRef.current && want.boardX) stripRef.current.scrollLeft = want.boardX;
        for (const [stageId, y] of Object.entries(want.columnY || {})) {
          const el = columnRefs.current[stageId];
          if (el && y) el.scrollTop = y;
        }
      } catch { /* a detached node — the board is still correct, just at the top */ }
    };
    // One frame later: the column bodies have to exist and be laid out before a
    // scrollTop means anything. rAF is unavailable in some test environments,
    // so fall back rather than skipping the restore entirely.
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(apply);
    } else {
      apply();
    }
  }, [state.loading, state.columns, extra]);

  const loadMore = useCallback(async (col) => {
    const loaded = (col.cards.length + (extra[col.stageId] || []).length);
    setLoadingMore((m) => ({ ...m, [col.stageId]: true }));
    try {
      const r = await window.zignagenixAPI.invoke('crm:pipeline:board:column', pipelineId, col.stageId, loaded, PAGE_SIZE,
        // ⚠ The SAME selection as the first page. Paging a column with a
        // different status set would mix two filters in one column and the
        // counts would stop adding up.
        { ownerId: ownerFilter, search: searchFilter, statuses });
      const got = (r && r.cards) || [];
      setExtra((x) => ({ ...x, [col.stageId]: [...(x[col.stageId] || []), ...got] }));
      // Record how deep this column has been paged, so a return trip can refill
      // it before putting the scroll offset back. Counted from what ARRIVED,
      // not from what was asked for — a short last page must not make the
      // refill ask for rows the server does not have.
      report((p) => ({
        ...p,
        stageId: String(col.stageId),
        loadedExtra: { ...(p.loadedExtra || {}), [col.stageId]: ((p.loadedExtra || {})[col.stageId] || 0) + got.length },
      }));
    } finally {
      setLoadingMore((m) => ({ ...m, [col.stageId]: false }));
    }
  }, [pipelineId, extra, ownerFilter, searchFilter, report]);

  const { live, terminal } = useMemo(() => {
    const cols = [...(state.columns || [])].sort((a, b) => (a.sortOrder - b.sortOrder) || (a.stageId - b.stageId));
    return { live: cols.filter((c) => !c.terminal), terminal: cols.filter((c) => c.terminal) };
  }, [state.columns]);

  if (state.loading) return <div style={{ padding: 16, fontSize: 12, color: '#6C757D' }}>Loading board…</div>;
  if (state.error) return <div style={{ padding: 16, fontSize: 12, color: '#DC3545' }}>Could not load board: {state.error}</div>;

  const renderColumn = (col) => {
    const cards = [...col.cards, ...(extra[col.stageId] || [])];
    const remaining = col.count - cards.length;
    const isPlace = !!(place && String(place.stageId) === String(col.stageId));
    return (
      <div key={col.stageId} data-stage-column data-stage-id={col.stageId} tabIndex={0}
        onFocus={() => noteStage(col.stageId)}
        style={{
          minWidth: 230, width: 230, flex: '0 0 230px', background: '#fff', borderRadius: 12,
          // The column the person was last reading is outlined when they come
          // back, so "where was I" is answered by the screen and not by memory.
          border: `2px solid ${col.color || '#2E8B98'}${isPlace ? 'CC' : '30'}`,
          display: 'flex', flexDirection: 'column',
          maxHeight: 460, outline: 'none',
        }}>
        <div style={{ padding: '10px 12px', borderBottom: `2px solid ${col.color || '#2E8B98'}`, background: `${col.color || '#2E8B98'}08`, borderRadius: '10px 10px 0 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
            <span title={col.name} style={{ fontSize: 11, fontWeight: 700, color: col.color || '#2E8B98', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{col.name}</span>
            <span style={{ fontSize: 10, fontWeight: 700, background: `${col.color || '#2E8B98'}18`, color: col.color || '#2E8B98', padding: '1px 6px', borderRadius: 8 }}>{col.count}</span>
          </div>
          {/* Summed value per column, WITH coverage — GHL shows the sum and our
              board showed neither. */}
          <div style={{ fontSize: 10, color: '#6C757D', marginTop: 2 }}>
            <MoneyWithCoverage sum={col.sumValue} withValue={col.withValue} count={col.count} />
            {!col.withValue && <span>no value recorded on any of {col.count}</span>}
          </div>
          {!col.resolvable && (
            <div style={{ fontSize: 9, color: '#B45309', marginTop: 2 }}>stage not mapped to GHL</div>
          )}
        </div>
        <div
          ref={(el) => { columnRefs.current[col.stageId] = el; }}
          data-stage-scroller
          onScroll={(e) => {
            const y = e.currentTarget.scrollTop;
            report((p) => ({
              ...p,
              stageId: String(col.stageId),
              columnY: { ...(p.columnY || {}), [col.stageId]: y },
            }));
          }}
          style={{ flex: 1, overflowY: 'auto', padding: 6 }}>
          {cards.length === 0 ? (
            <div style={{ fontSize: 10, color: '#ADB5BD', textAlign: 'center', padding: 16 }}>No opportunities</div>
          ) : cards.map((c) => (
            <Card key={c.id} card={c} onOpen={onContactClick} onFocusStage={() => noteStage(col.stageId)} />
          ))}
          {remaining > 0 && (
            <button onClick={() => loadMore(col)} disabled={!!loadingMore[col.stageId]}
              style={{ width: '100%', fontSize: 10, padding: '6px 4px', borderRadius: 8, border: 'none', background: '#F1F5F9', color: '#2E8B98', fontWeight: 600, cursor: 'pointer' }}>
              {loadingMore[col.stageId] ? 'Loading…' : `Load more (${remaining} remaining)`}
            </button>
          )}
        </div>
      </div>
    );
  };

  const hidingNote = hiddenStatusNote(statuses);

  return (
    <div>
      {/*
        ⛔⛔ THE FILTER THAT WAS ALWAYS THERE, NOW ON SCREEN — Card P117 PR-B.

        Maria could not tell active from inactive deals on this board, and the
        reason was stronger than a missing badge: both board handlers
        hard-coded `status = 'open'`, so closed deals were not muted — they
        were ABSENT, and nothing said so. The chips default to exactly that
        selection, so the board opens as it always has; what is new is that it
        admits what it is doing and lets her change it.

        ⚠ The "Hiding …" line is the honest half. Chips alone show what IS
        selected; a person scanning a board needs to be told what is NOT.
      */}
      <div data-board-status-filter
        style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        <span style={{ fontSize: 11, color: '#6C757D', fontWeight: 600 }}>Status</span>
        {OPPORTUNITY_STATUSES.map((sKey) => {
          const on = statuses.includes(sKey);
          const meta = STATUS_META[sKey];
          return (
            <button
              key={sKey}
              type="button"
              data-status-chip={sKey}
              aria-pressed={on}
              onClick={() => toggleStatus(sKey)}
              style={{
                padding: '3px 10px', borderRadius: 999, cursor: 'pointer',
                fontSize: 11, fontWeight: 700,
                background: on ? meta.bg : '#FFFFFF',
                color: on ? meta.color : '#94A3B8',
                border: `1px solid ${on ? meta.color : '#E2E8F0'}`,
              }}
            >
              {meta.label}
            </button>
          );
        })}
        {hidingNote && (
          <span data-hiding-note style={{ fontSize: 11, color: '#92400E', background: '#FEF3C7',
            border: '1px solid #FCD34D', borderRadius: 8, padding: '2px 8px' }}>
            {hidingNote}
          </span>
        )}
      </div>
      <div
        ref={stripRef}
        data-board-strip
        onScroll={(e) => {
          const x = e.currentTarget.scrollLeft;
          report((p) => ({ ...p, boardX: x }));
        }}
        style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 10, minHeight: 240 }}>
        {live.map(renderColumn)}
      </div>

      {/* ─── Terminal columns: COLLAPSED, never hidden ───────────────────────
          44.1% of open opportunities (3,411 of 7,730, measured 2026-09-06) sit
          on a stage that reads as finished. Rendering them inline makes the
          board filled and useless; hiding them repeats the defect #468 fixed.
          They are collapsed with their counts visible, and each says WHY. */}
      {terminal.length > 0 && (
        <div style={{ marginTop: 12, borderTop: '1px dashed #CBD5E1', paddingTop: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748B', marginBottom: 6 }}>
            Finished / closed stages — {terminal.reduce((a, c) => a + c.count, 0)} opportunities, collapsed
            {'  '}
            <MoneyWithCoverage
              sum={terminal.reduce((a, c) => a + (c.sumValue || 0), 0)}
              withValue={terminal.reduce((a, c) => a + (c.withValue || 0), 0)}
              count={terminal.reduce((a, c) => a + c.count, 0)} />
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {terminal.map((col) => (
              <button key={col.stageId}
                onClick={() => setExpandedTerminal((m) => ({ ...m, [col.stageId]: !m[col.stageId] }))}
                title={basisLabel[col.terminalBasis] || ''}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600,
                  padding: '5px 10px', borderRadius: 10, border: '1px solid #E2E8F0',
                  background: '#F8FAFC', color: '#475569', cursor: 'pointer',
                }}>
                {expandedTerminal[col.stageId] ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                {col.name}
                <span style={{ background: '#E2E8F0', borderRadius: 8, padding: '0 6px' }}>{col.count}</span>
                <MoneyWithCoverage sum={col.sumValue} withValue={col.withValue} count={col.count}
                  style={{ color: '#15803D' }} />
                {col.terminalBasis === 'name_fallback' && <span title={basisLabel.name_fallback} style={{ color: '#B45309' }}>⚠</span>}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10, overflowX: 'auto', marginTop: 10 }}>
            {terminal.filter((c) => expandedTerminal[c.stageId]).map(renderColumn)}
          </div>
        </div>
      )}

      {state.unplacedCount > 0 && (
        <div style={{ marginTop: 10, fontSize: 11, color: '#B45309' }}>
          ⚠ {state.unplacedCount} opportunit{state.unplacedCount === 1 ? 'y' : 'ies'} sit on a GHL stage this
          pipeline does not declare, so they appear in no column.
        </div>
      )}
    </div>
  );
}
